/**
 * Contrato de transporte entre el SPA, la capa de Pages Functions y el Worker.
 *
 * Vive en el paquete compartido porque los tres bordes tienen que coincidir en el mismo
 * texto: el SPA construye la ruta, la Function la reescribe hacia el enlace de servicio y
 * el Worker decide si la atiende. Un prefijo o un nombre de cabecera escrito tres veces es
 * un contrato que se rompe en el primer cambio; escrito una vez, no puede.
 *
 * Nada de aquí toca el entorno: son literales y formas de datos. El paquete compartido
 * sigue siendo cálculo puro y el límite del linter no se roza.
 */

/**
 * Prefijo de versión del contrato de la API. El SPA nunca escribe rutas absolutas: pide
 * `/api/v1/...` al mismo origen, de modo que la cookie de sesión viaja sin relajar ningún
 * atributo y no hay CORS que resolver.
 */
export const PREFIJO_API = '/api/v1';

/**
 * Versión del contrato de datos compartido entre el SPA y el Worker. El SPA la envía en la
 * cabecera `X-AISalud-Client-Version` y el Worker rechaza con `409` las versiones que ya no
 * soporta.
 */
export const SHARED_CONTRACT_VERSION = 'v1';

/**
 * Versiones del contrato que el Worker atiende. **Única declaración**: el eslabón que emite el
 * `409` lee esta lista y no arma la suya, así que no existe un segundo lugar donde una versión
 * pueda quedar soportada a medias.
 *
 * Vive en el paquete compartido y no en el Worker porque los dos lados la necesitan: el Worker
 * para decidir el rechazo y el SPA para saber qué declara. Que sea el mismo texto es el punto.
 *
 * Hoy una sola. Cuando exista `v2`, la lista lleva las dos durante la transición y `v1` sale
 * cuando el paquete que la usaba ya no está en ningún navegador: quitarla antes es exactamente el
 * `409` masivo que la comprobación existe para evitar.
 */
export const VERSIONES_DE_CONTRATO_SOPORTADAS: readonly string[] = [SHARED_CONTRACT_VERSION];

/**
 * Origen sintético con el que la capa de Pages invoca al Worker por el enlace de servicio.
 *
 * El Worker se despliega con `workers_dev = false` y sin ruta de zona, así que no tiene
 * URL pública: la única vía de entrada es `env.API.fetch()`. Este origen es la marca de esa
 * vía, y el Worker rechaza cualquier invocación que llegue con otro. No es una precaución
 * cosmética sino parte del modelo de autorización: el Worker solo confía en las cabeceras
 * internas de identidad cuando la petición llegó por el enlace.
 *
 * El dominio `.internal` es reservado y no resoluble en la red pública, de modo que ni por
 * error puede convertirse en una llamada de salida.
 */
export const ORIGEN_INTERNO_DEL_WORKER = 'https://aisalud-api.internal';

/**
 * Cabeceras del contrato, con el nombre exacto que viaja por el cable.
 *
 * - `peticionDeAplicacion`: marca de que la petición la originó el guion del SPA y no una
 *   navegación ni un formulario de otro sitio. Su valor no importa, su presencia sí: un
 *   formulario entre sitios no puede añadir cabeceras propias y una petición de otro origen
 *   necesitaría un comprobante previo que nunca va a obtener. Es la verificación que el
 *   `csrfGuard` exige en toda mutación, detrás de `SameSite=Strict`.
 * - `versionDeCliente`: versión del contrato que trae el paquete cargado en el navegador.
 *   Los dos despliegues no son atómicos, así que el Worker responde `409` cuando ya no la
 *   soporta y el SPA lo traduce en un aviso de recarga.
 * - `idDePeticion`: identificador único por petición. Lo emite la capa de Pages, viaja al
 *   Worker y vuelve en la respuesta, de modo que la persona puede citarlo al pedir soporte.
 */
export const CABECERAS = {
  peticionDeAplicacion: 'X-AISalud-Request',
  versionDeCliente: 'X-AISalud-Client-Version',
  idDePeticion: 'X-Request-Id',
} as const;

/**
 * Prefijo de las cabeceras internas que la capa de Pages añade y el Worker consume.
 *
 * La regla que lo hace seguro tiene dos mitades y las dos son obligatorias: la Function
 * **borra** toda cabecera con este prefijo que venga del navegador antes de reenviar, y el
 * Worker solo las lee cuando la petición llegó por el enlace de servicio. Sin la primera,
 * cualquiera se declararía dueño de otra cuenta escribiendo una cabecera.
 */
export const PREFIJO_CABECERAS_INTERNAS = 'x-aisalud-internal-';

/**
 * Las cuatro cabeceras internas, con el nombre exacto que viaja por el enlace de servicio.
 *
 * Viven aquí y no en el Worker porque las escribe la capa de Pages y las lee el Worker, y son
 * los dos únicos participantes: un nombre escrito dos veces es un nombre que se desincroniza en
 * el primer cambio. El límite del linter impide que la capa de Pages importe `domain/` o
 * `repositories/` del Worker, así que el paquete compartido es el único terreno común.
 *
 * - `actorId` y `actorRol`: identidad ya resuelta contra la tabla `session`. El Worker les cree
 *   **solo** después del guardián del enlace de servicio.
 * - `motivoDeSesion`: por qué no hay identidad, cuando no la hay. Distingue «no presentó cookie»
 *   de «presentó una que no resuelve», y con eso el Worker elige entre `session_required` y
 *   `session_expired` sin volver a consultar nada. Las dos son `401`, así que confiar en la
 *   cabecera para elegir el código no abre ninguna puerta.
 * - `origenInseguro`: el navegador habló por HTTP en claro, que solo ocurre en `localhost`. Es lo
 *   que permite emitir la cookie de sesión sin `Secure` en desarrollo local, y el Worker no puede
 *   deducirlo porque la URL que recibe ya es la interna.
 */
export const CABECERAS_INTERNAS = {
  actorId: `${PREFIJO_CABECERAS_INTERNAS}actor-id`,
  actorRol: `${PREFIJO_CABECERAS_INTERNAS}actor-role`,
  motivoDeSesion: `${PREFIJO_CABECERAS_INTERNAS}session-reason`,
  origenInseguro: `${PREFIJO_CABECERAS_INTERNAS}insecure-origin`,
} as const;

/**
 * Por qué la capa de Pages no reenvió identidad. Conjunto cerrado, apto para el registro.
 *
 * `no_resuelve` cubre las cuatro causas que la política de sesión distingue por dentro —fila
 * inexistente, vencida, revocada y reemplazada fuera de tolerancia— y las colapsa a una sola
 * hacia el Worker, por la misma razón por la que el Worker las colapsa hacia afuera: decirle a
 * quien presenta un testigo en qué estado exacto está la fila es un oráculo sobre la cuenta
 * ajena que está probando.
 */
export const MOTIVOS_DE_SESION = ['ausente', 'no_resuelve'] as const;

/** Uno de {@link MOTIVOS_DE_SESION}. */
export type MotivoDeSesionDelBorde = (typeof MOTIVOS_DE_SESION)[number];

/**
 * Segundos que un testigo ya rotado sigue sirviendo para las peticiones que iban en vuelo
 * cuando se rotó: 60, como fija el diseño.
 *
 * Es el mismo valor que `TOLERANCIA_DE_REEMPLAZO_SEGUNDOS` de la política de sesión del Worker,
 * y está aquí porque la capa de Pages lo necesita para su consulta de resolución y no puede
 * importar `domain/` del Worker. La igualdad entre los dos no se deja a la buena voluntad: una
 * prueba del anillo de runtime la afirma, y si alguien cambia uno sin el otro esa prueba falla.
 *
 * La tolerancia se aplica **solo** a la sesión rotada, la que tiene reemplazo. Una revocada sin
 * reemplazo es un cierre de sesión y se rechaza desde el primer segundo, que es lo que exige el
 * criterio 1.8.
 */
export const TOLERANCIA_DE_TESTIGO_ROTADO_SEGUNDOS = 60;

/**
 * Los tres códigos estables que el SPA **y** el Worker tienen que nombrar, con su única
 * declaración.
 *
 * El resto de la taxonomía vive en el mapeador de errores del Worker y ahí se queda: el SPA no lo
 * interpreta, solo muestra el `message`. Estos tres son distintos porque los dos lados deciden algo
 * con ellos —el Worker los emite y el SPA cambia de pantalla— y escritos dos veces son un contrato
 * que se desincroniza en el primer cambio de nombre, sin que nada falle hasta que alguien mire.
 *
 * `session_required` y `session_expired` son los dos `401` de la sesión perdida, y la distinción
 * importa: el `401` de credenciales equivocadas es `invalid_credentials` y **no** redirige, o
 * escribir mal la contraseña recargaría el ingreso y borraría el error antes de que nadie lo lea.
 */
export const CODIGO_DE_SESION_REQUERIDA = 'session_required';

/** El `401` de quien presentó una cookie que ya no resuelve. Ver {@link CODIGO_DE_SESION_REQUERIDA}. */
export const CODIGO_DE_SESION_EXPIRADA = 'session_expired';

/** Los dos `401` que significan sesión perdida, y no credenciales equivocadas. */
export const CODIGOS_DE_SESION_PERDIDA: readonly string[] = [
  CODIGO_DE_SESION_REQUERIDA,
  CODIGO_DE_SESION_EXPIRADA,
];

/**
 * Código del `409` con que el Worker declara obsoleto el paquete cargado en el navegador.
 *
 * `409` y no un error de validación porque es un conflicto de estado entre dos despliegues que no
 * son atómicos, y el aviso correcto no es «revisa lo que enviaste» sino «recarga la página».
 */
export const CODIGO_DE_CLIENTE_OBSOLETO = 'client_version_unsupported';

/**
 * Forma única de la respuesta de error de la API. La produce el mapeador de errores del
 * Worker y ninguna ruta arma la suya.
 *
 * `code` es lo que interpreta el SPA y nunca se traduce; `message` es lo que se muestra,
 * siempre en español de Chile; `requestId` es lo que permite encontrar la traza. `details`
 * aparece solo en errores de validación y lleva la ruta del campo y el motivo, jamás el
 * valor rechazado, porque un valor rechazado puede ser un dato clínico.
 */
export interface SobreDeError {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly requestId: string;
    readonly details?: readonly unknown[];

    /**
     * Versión del consentimiento que falta aceptar. Aparece **solo** en el `428` de la
     * compuerta de consentimiento (criterios 4.1, 4.3).
     *
     * Va como campo propio y con nombre en lugar de entrar en `details`, que es de los
     * errores de validación y lleva rutas de campo: el SPA la lee sin conversiones para
     * abrir el flujo de aceptación de esa versión y volver después a la acción original. Es
     * una etiqueta de versión, así que no transporta ningún dato de ninguna persona.
     */
    readonly pendingConsentVersion?: string;

    /**
     * Columnas que el archivo de importación debería traer. Aparece **solo** en el rechazo del
     * archivo completo de `POST /glucose/import` (criterio 8.6).
     *
     * El criterio manda «rechazar el archivo completo **e indicar la estructura esperada**», así
     * que la lista tiene que viajar en la respuesta: sin ella la persona sabe que su archivo no
     * sirve y no sabe cómo arreglarlo. Va como campo propio y con nombre por la misma razón que
     * {@link SobreDeError.error.pendingConsentVersion}: `details` lleva rutas de campo de un
     * esquema, y estos son nombres de columna de un formato de archivo.
     *
     * Son literales del código —los nombres canónicos que declara el formato—, nunca contenido del
     * archivo cargado, así que no transportan ningún dato de ninguna persona.
     */
    readonly expectedColumns?: readonly string[];

    /**
     * Columnas obligatorias que faltaban en el archivo cargado (criterio 8.6).
     *
     * Vacío cuando el rechazo no es de columnas —el archivo excede el tamaño admitido, viene vacío
     * o tiene un campo entrecomillado sin cerrar—, porque ahí no falta ninguna. Salen de la misma
     * lista cerrada que {@link SobreDeError.error.expectedColumns} y son igual de inocuas.
     */
    readonly missingColumns?: readonly string[];

    /**
     * Instante en que vence el plazo de la eliminación de cuenta, en segundos UTC. Aparece **solo**
     * en el `403` de la cuenta pendiente de eliminación (criterio 4.6).
     *
     * Va como campo propio y con nombre por la misma razón que
     * {@link SobreDeError.error.pendingConsentVersion}: el SPA lo lee sin conversiones para
     * presentar el estado de la solicitud y el plazo restante, que es lo que el criterio pide
     * mostrar «en lugar de un error de acceso sin explicación». Sin él, el código estable diría que
     * la cuenta está bloqueada y no cuánto falta.
     *
     * **Es el vencimiento y no los segundos que quedan.** Es el mismo instante que guarda la fila de
     * la cuenta y que guarda la solicitud, así que dos respuestas seguidas dicen exactamente lo
     * mismo y una respuesta guardada no envejece hacia un plazo que no es. La cuenta atrás la deriva
     * quien la muestra, que es también quien sabe en qué zona la presenta.
     *
     * No transporta ningún dato clínico ni de contacto: es una marca temporal de la propia cuenta
     * que pide la eliminación, y la recibe esa misma cuenta.
     */
    readonly deletionDueAt?: number;
  };
}

/**
 * Documento del recurso de estado (`GET /api/v1/health`, criterio 27.2).
 *
 * Es un informe de estado y no una respuesta de error: por eso no usa el sobre de error ni
 * cuando la base no responde. Lo consume la vigilancia de operación, que necesita leer
 * `database.reachable` para distinguir un Worker vivo con base caída de un Worker caído.
 */
export interface EstadoDeSalud {
  readonly status: 'ok' | 'degraded';
  /** `APP_VERSION` del entorno que atendió la petición. */
  readonly version: string;
  /** Versión del contrato que ese Worker soporta. */
  readonly contract: string;
  readonly database: {
    readonly reachable: boolean;
    readonly latencyMs: number;
  };
  readonly requestId: string;
}
